import { supabase } from '@/lib/supabase';
import type { OrgIntegration, OrgWhatsappAccount } from '@/types';

const PARTNER_TOKEN = import.meta.env.VITE_GREENAPI_PARTNER_TOKEN as string;
const GREENAPI_PARTNER_URL = 'https://api.green-api.com/partner';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GreenApiInstance {
  idInstance: number;
  name: string;
  typeAccount: string;
  stateInstance: string;
}

// ── Integrations API ──────────────────────────────────────────────────────────

export const integrationsApi = {
  /** List integrations for an org (admin: pass orgId explicitly) */
  getForOrg: async (orgId: string): Promise<OrgIntegration[]> => {
    const { data, error } = await supabase
      .from('org_integrations')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data as OrgIntegration[];
  },

  /** Upsert an integration config */
  upsert: async (payload: Partial<OrgIntegration> & { organization_id: string; provider: string }): Promise<OrgIntegration> => {
    const { data, error } = await supabase
      .from('org_integrations')
      .upsert(payload, { onConflict: 'organization_id,provider' })
      .select()
      .single();
    if (error) throw error;
    return data as OrgIntegration;
  },

  /** Deactivate an integration */
  deactivate: async (orgId: string, provider: string): Promise<void> => {
    const { error } = await supabase
      .from('org_integrations')
      .update({ is_active: false })
      .eq('organization_id', orgId)
      .eq('provider', provider);
    if (error) throw error;
  },

  // ── WhatsApp / GreenAPI ──────────────────────────────────────────────────

  /** List WhatsApp accounts for a specific org */
  getWhatsappAccounts: async (orgId: string): Promise<OrgWhatsappAccount[]> => {
    const { data, error } = await supabase
      .from('org_whatsapp_accounts')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data as OrgWhatsappAccount[];
  },

  /** Provision a new GreenAPI instance for the org via Partner API, then save to DB */
  provisionGreenApiInstance: async (orgId: string, name: string): Promise<OrgWhatsappAccount> => {
    const res = await fetch(`${GREENAPI_PARTNER_URL}/createInstance/${PARTNER_TOKEN}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) {
      const err = await res.text();
      throw new Error(`GreenAPI Partner error: ${err}`);
    }
    const instance = await res.json() as { idInstance: number; apiTokenInstance: string };

    const { data, error } = await supabase
      .from('org_whatsapp_accounts')
      .insert({
        organization_id: orgId,
        name,
        instance_id: String(instance.idInstance),
        api_token: instance.apiTokenInstance,
        type: 'greenapi_unofficial',
        purpose: 'both',
        status: 'pending_scan',
      })
      .select()
      .single();
    if (error) throw error;
    return data as OrgWhatsappAccount;
  },

  /** Delete a WhatsApp account from GreenAPI then from DB */
  deleteWhatsappAccount: async (orgId: string, accountId: string): Promise<void> => {
    const { data: account, error: fetchErr } = await supabase
      .from('org_whatsapp_accounts')
      .select('instance_id')
      .eq('id', accountId)
      .eq('organization_id', orgId)
      .single();
    if (fetchErr || !account) throw new Error('WhatsApp account not found');

    try {
      await fetch(`${GREENAPI_PARTNER_URL}/deleteInstance/${PARTNER_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idInstance: Number(account.instance_id) }),
      });
    } catch {
      console.warn('GreenAPI delete failed, removing from DB anyway');
    }

    const { error } = await supabase
      .from('org_whatsapp_accounts')
      .delete()
      .eq('id', accountId)
      .eq('organization_id', orgId);
    if (error) throw error;
  },

  // ── GreenAPI Partner (raw access) ────────────────────────────────────────

  greenapi: {
    getInstances: async (): Promise<GreenApiInstance[]> => {
      const res = await fetch(`${GREENAPI_PARTNER_URL}/getInstances/${PARTNER_TOKEN}`);
      if (!res.ok) throw new Error('Failed to fetch GreenAPI instances');
      return res.json() as Promise<GreenApiInstance[]>;
    },

    createInstance: async (name: string): Promise<{ idInstance: number; apiTokenInstance: string }> => {
      const res = await fetch(`${GREENAPI_PARTNER_URL}/createInstance/${PARTNER_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error('Failed to create GreenAPI instance');
      return res.json();
    },

    deleteInstance: async (idInstance: number): Promise<void> => {
      const res = await fetch(`${GREENAPI_PARTNER_URL}/deleteInstance/${PARTNER_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idInstance }),
      });
      if (!res.ok) throw new Error('Failed to delete GreenAPI instance');
    },
  },

  // ── SendPulse ────────────────────────────────────────────────────────────

  sendpulse: {
    generatePartnerLink: (orgSlug: string): string =>
      `https://sendpulse.com/ref/partner?ref=otomator&slug=${encodeURIComponent(orgSlug)}`,
  },

  // ── SMS 019 ──────────────────────────────────────────────────────────────

  sms019: {
    PACKAGES: [
      { id: 'sms_100',  name: '100 SMS',   price_nis: 49,  sms_count: 100  },
      { id: 'sms_500',  name: '500 SMS',   price_nis: 199, sms_count: 500  },
      { id: 'sms_1000', name: '1,000 SMS', price_nis: 349, sms_count: 1000 },
      { id: 'sms_5000', name: '5,000 SMS', price_nis: 999, sms_count: 5000 },
    ],
  },
};
